<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex flex-column">
        <div class="am-h2">Dissolution d'une association</div>
        <template v-if="!tribunal">
          <div class="accent-block mt-4 mb-4">
            <div class="am-t2">
              Vous souhaitez dissoudre votre association.
            </div>
            <div class="am-t2">Merci de compléter le formulaire ci-dessous en naviguant via les différents onglets.</div>
          </div>
        </template>
        <a-association-toolbar
          :numero="numero"
          :juridiction="juridictionOrigine"
          :value="currentVersionAssociation"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-fade-transition mode="out-in">
          <a-fin-creation-sheet
            v-if="tribunal"
            :projection="tribunal"
            mode="dissolution"
          />
          <v-stepper v-else alt-labels v-model="step">
            <v-stepper-header>
              <v-stepper-step
                v-for="s of stepsEditable"
                edit-icon="$complete"
                :key="s.step + 's'"
                :step="s.step"
                :editable="s.editable && !working"
                :rules="s.rules"
              >
                {{ s.text }}
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content :step="1">
                <v-form ref="declarant" autocomplete="false">
                  <a-declarant-sheet v-model="item.declarant" />
                  <div class="d-flex justify-center mt-2">
                    <v-btn data-testid="suivant" @click="suivant('declarant')" class="primary"> Suivant </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="2">
                <v-form ref="dissolution">
                  <a-dissolution-sheet v-model="item.dissolution" />
                  <div class="d-flex justify-center">
                    <v-btn data-testid="suivant" @click="suivant('dissolution')" class="primary">
                      Suivant
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="3">
                <v-form ref="annexes">
                  <a-annexe-sheet
                    v-model="item.annexes"
                    @clearValidation="clearAnnexeDocument"
                    @validate="validateAnnexes"
                    @disabled="updateAnnexes"
                    dissolution
                    :codePostal="codePostalAssociation"
                  />
                  <div class="d-flex justify-center">
                    <v-btn
                      @click="suivant('annexes')"
                      class="primary"
                      :disabled="annexesDisabled"
                      data-testid="suivant"
                    >
                      Suivant
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="4">
                <v-form ref="validation" :readonly="working">
                  <div class="accent-block mt-4 mb-4">
                    <p>
                        {{libelleJuridiction}}
                    </p>
                    <p>
                      Merci de vérifier vos informations avant de soumettre votre
                      formulaire.
                    </p>
                  </div>
                  <v-textarea
                    label="Observation"
                    filled
                    v-model="item.commentaire"
                    hint="Pour toute information complémentaire"
                    persistent-hint
                    :rules="[serverRule('commentaire')]"
                    data-testid="observation"
                  />
                  <div class="d-flex justify-center mt-2">
                    <v-btn data-testid="soumettre" class="primary" @click="soumettre" :loading="working">
                      Soumettre
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-fade-transition>
      </v-col>
    </v-row>
    <a-confirm-navigation-dialog ref="confirm-navigation" />
  </v-container>
</template>

<script>
import makeRemoteWorker from "@/mixins/makeRemoteWorker";
import makeNavigationGuard from "@/mixins/makeNavigationGuard";
import { requeteService } from "@/services";
import copy from "@/services/copy";
import makeRules from "@/mixins/makeRules";
import { mapGetters } from "vuex";
import { toDetailAssociation } from "@/routes/associations";

export default {
  mixins: [makeRemoteWorker(), makeNavigationGuard(), makeRules({ inject: false })],

  props: {
    numero: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      item: {
        declarant: {},
        association: {},
        dissolution: {},
        annexes: { annexes: [] },
        commentaire: null,
      },
      membresDisabled: true,
      annexesDisabled: true,
      tribunal: null,
      informationsAvant: null,
      etatAvant: null,
      step: 1,
      maxStep: 0,
      steps: [
        { text: "Déclarant", rules: [this.stepperRule("declarant")] },
        { text: "Dissolution", rules: [this.stepperRule("dissolution")] },
        { text: "Annexes", rules: [this.stepperRule("documents")] },
        { text: "Soumission" },
      ].map(({ text, rules }, index) => ({
        step: index + 1,
        text,
        rules,
      })),
    };
  },
  mounted() {
    if (this.numero === this.numeroOrigine) {
      this.etatAvant = this.etatOrigine;
      this.informationsAvant = copy(this.informationsOrigine);
      this.item.association = copy(this.informationsOrigine);
      this.item.dissolution = {
        decision: null,
        commentaireLiquidation: null,
        liquidateurs: {
          physiques: [],
          morales: [],
        },
      };
      this.$nextTick(() => this.initSnapshot());
    } else {
      this.$nextTick(() => {
        this.initSnapshot();
        this.$router.push(toDetailAssociation(this.numero));
      });
    }
  },
  computed: {
    currentVersionAssociation() {
      return {
        informations: this.informationsAvant,
        etatAssociation: this.etatAvant,
      };
    },
    stepsEditable() {
      return this.steps.map((s, index) => ({
        ...s,
        editable: index < this.step,
      }));
    },
    ...mapGetters({
      informationsOrigine: "modificationAssociation/informations",
      etatOrigine: "modificationAssociation/etat",
      numeroOrigine: "modificationAssociation/numero",
      juridictionOrigine: "modificationAssociation/juridiction",
    }),
    libelleJuridiction() {
      return `La juridiction compétente pour traiter votre demande est le ${this.juridictionOrigine}.`;
    },
    codePostalAssociation() {
      return this.item.association?.adresse?.codePostal;
    },
  },

  methods: {
    updateAnnexes(disabled) {
      this.annexesDisabled = disabled;
    },
    stepperRule(prefix) {
      return () => !Object.keys(this.validation?.fields).some(k => k.startsWith(prefix));
    },
    suivant(form) {
      this.clearValidation(form);

      if (this.$refs[form].validate()) {
        this.maxStep = Math.max(this.step, this.maxStep);
        this.step = this.step + 1;
      }
    },
    clearAnnexeDocument(index) {
      this.nettoyeValidation(f => f.startsWith(`documents[${index}]`));
      this.$refs["annexes"].validate();
    },
    clearValidation(form) {
      if ("declarant" === form) {
        this.nettoyeValidation(f => f.startsWith("declarant"));
      } else if ("dissolution" === form) {
        this.nettoyeValidation(f => f.startsWith("dissolution"));
      } else if ("annexes" === form) {
        this.nettoyeValidation(f => f.startsWith("documents"));
      }
    },
    validateAnnexes() {
      this.$refs.annexes.validate();
    },
    soumettre() {
      const item = {
        declarant: this.item.declarant,
        annexes: this.item.annexes.annexes,
        dissolution: this.item.dissolution,
        commentaire: this.item.commentaire,
      };
      this.doWork({
        forms: [
          this.$refs.declarant,
          this.$refs.dissolution,
          this.$refs.annexes,
          this.$refs.validation,
        ],
        work() {
          return requeteService.dissoudre(this.numero, item);
        },
        callback(value) {
          this.$store.commit("modificationAssociation/clear");
          this.initSnapshot();
          this.tribunal = value;
        },
        successText: "La requête a été créée",
        errorText: "Erreur durant la création de la requête",
      });
    },
  },
};
</script>
